import React, { useCallback, useState } from 'react'
import { ScreenReaderOnly } from '@moonpig/launchpad-components'
import {
  ProductMessageItem,
  ProductsChatMessage,
} from '../../ChatMessages/types'
import { AddToBasketAlert } from '../AddToBasketAlert'
import { ConciergeCardTile } from '../ConciergeCardTile'
import { ConciergeGiftTile } from '../ConciergeGiftTile'
import { ProductsCarousel } from '../ProductsCarousel'
import { useLocaleText } from '../../locale'
import {
  StyledProductsCarouselOverflow,
  StyledProductsMessage,
  StyledProductsTitle,
} from './styled'
import { useChatModeContext } from '../../ChatContext'
import { useProductsMessageTracking } from '../../hooks'

export const ProductsMessage = ({
  msg,
  id,
}: {
  msg: ProductsChatMessage
  id: number
}) => {
  const { switchToProductMode, setSelectedProductId } = useChatModeContext()
  const t = useLocaleText()
  const [confirmationMessages, setConfirmationMessages] = useState<
    { message: string; variant: 'success' | 'error' }[]
  >([])
  const productsContent = msg.content
  const labelId = `chat-products-label-${id}`
  const trackProductClick = useProductsMessageTracking({
    productsContent,
    messageId: id,
  })

  const handleProductClick = useCallback(
    (item: ProductMessageItem) => {
      trackProductClick(item)
      switchToProductMode()
      setSelectedProductId(item.id)
    },
    [trackProductClick, switchToProductMode, setSelectedProductId],
  )

  const showAddToBasketSuccessAlert = useCallback(() => {
    setConfirmationMessages([
      {
        message: t('concierge_chat.add_to_basket_success'),
        variant: 'success',
      },
    ])
  }, [t])

  const showAddToBasketFailureAlert = useCallback(() => {
    setConfirmationMessages([
      {
        message: t('concierge_chat.error_message'),
        variant: 'error',
      },
    ])
  }, [t])
  const hideBasketAlerts = useCallback(() => {
    setConfirmationMessages([])
  }, [])

  return (
    <StyledProductsMessage
      aria-labelledby={labelId}
      data-testid="chat-products-container"
      role="article"
    >
      <ScreenReaderOnly id={labelId}>
        {t('concierge_chat.screen_reader_assistant_label')}
      </ScreenReaderOnly>
      {productsContent.title && (
        <StyledProductsTitle>{productsContent.title}</StyledProductsTitle>
      )}
      <StyledProductsCarouselOverflow>
        <ProductsCarousel
          ariaLabel={t('concierge_chat.products_carousel_title')}
        >
          {productsContent.items.map((item, itemId) =>
            item.productType === 'cards' ? (
              <ConciergeCardTile
                key={item.id}
                item={item}
                itemId={String(itemId)}
                onTileClick={handleProductClick}
              />
            ) : (
              <ConciergeGiftTile
                key={item.id}
                item={item}
                itemId={String(itemId)}
                onBasketAddStarted={hideBasketAlerts}
                onBasketAddFailed={showAddToBasketFailureAlert}
                onBasketAdded={showAddToBasketSuccessAlert}
                onTileClick={handleProductClick}
              />
            ),
          )}
        </ProductsCarousel>
      </StyledProductsCarouselOverflow>
      {confirmationMessages.map(({ message, variant }, index) => (
        <AddToBasketAlert
          key={`${message}-${index}`}
          message={message}
          variant={variant}
        />
      ))}
    </StyledProductsMessage>
  )
}
